import { CONFIG_SCHEMA } from '../../../shared/services/config';
import { getRoleAccessStatus } from '../../../shared/utils';
import { isEmpty } from '../../../shared/utils';

type RequiredAttributes =
  | 'capex_id'
  | 'poe'
  | 'anzustrebender_liefertermin'
  | 'psp_c_a_element_cmm'
  | 'psp_c_a_element_ncp'
  | 'psp_c_a_element_femi'
  | 'capex_stunden'
  | 'cmmhrs'
  | 'hardtooling_euro'
  | 'design_owner_id'
  | 'pf_lc';

const pspAccessRequiredAttributes: RequiredAttributes[] = [
  'capex_id',
  'poe',
  'anzustrebender_liefertermin',
  'psp_c_a_element_cmm',
  'psp_c_a_element_ncp',
  'psp_c_a_element_femi',
  'capex_stunden',
  'cmmhrs',
  'hardtooling_euro',
  'design_owner_id',
  'pf_lc',
];

const pspSyncPlaceholderValues = ['wird nachgereicht', 'Nicht benötigt'];

/**
 * Returns whether a current NPI Steering value can be overwritten by PSP sync.
 *
 * Append is allowed when value is empty (`null`, `undefined`, empty string)
 * or when it contains a known placeholder text.
 */
export function isPspSyncAppendableValue(val: unknown): boolean {
  if (isEmpty(val)) return true;
  if (typeof val === 'string') {
    const normalized = val.trim();
    return pspSyncPlaceholderValues.includes(normalized);
  }
  return false;
}

/**
 * Determines whether the current user can trigger PSP sync based on role access
 * and whether the required PSP key fields are present on the row.
 *
 * @param roleIds - Role IDs assigned to the current user.
 * @param row - Current row data used to validate required PSP key fields.
 * @param screenGridId - Screen grid ID for role lookup.
 * @returns `true` when the row has required keys and the user has an allowed role.
 */
export async function userHasPspAccess(
  roleIds: number[],
  row: Record<string, unknown>,
  screenGridId: number,
): Promise<boolean> {
  const hasRequiredKeys = pspAccessRequiredAttributes.every((attr) =>
    Object.prototype.hasOwnProperty.call(row ?? {}, attr),
  );
  if (!hasRequiredKeys) {
    return false;
  }

  const { hasAnyRole } = await getRoleAccessStatus({
    roleIds,
    roleNames: ['ME', 'AV', 'Administrator'],
    screenGridId,
    schema: CONFIG_SCHEMA,
    table: 'mbd_roles',
  });

  return hasAnyRole;
}
